import React, { useState } from 'react';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Sector } from 'recharts';
import { COLORS } from '../../../../../../utils';

type Props = {
    allocations: {
        key: string;
        value: number;
    }[];
    type: string;
};

const AllocationCharts: React.FC<Props> = ({ allocations, type }) => {
    const [activeIndex, setActiveIndex] = useState(0);

    const onPieEnter = (_: any, index: number) => {
        setActiveIndex(index);
    };

    return (
        <div className="w-full md:w-1/2 py-6">
            <p className="font-semibold text-sm text-center mb-4 lg:mb-0">{type}</p>
            <ResponsiveContainer width="100%" height={450}>
                <PieChart width={200} height={150} className="text-center">
                    <defs>
                        {allocations.map((entry, index) => (
                            <linearGradient
                                id={`linearGradient${index}`}
                                key={`${entry}_${index}`}
                                x1="1"
                                y1="1"
                                x2="0"
                                y2="0"
                            >
                                <stop
                                    offset="30%"
                                    stopColor={COLORS[index > COLORS.length ? index % COLORS.length : index]}
                                    stopOpacity={1}
                                />
                                <stop offset="90%" stopColor="#FFFFFF" stopOpacity={0.9} />
                            </linearGradient>
                        ))}
                    </defs>
                    <Pie
                        activeIndex={activeIndex}
                        data={allocations}
                        cx={'50%'}
                        innerRadius={80}
                        outerRadius={120}
                        fill="url(#colorUv)"
                        dataKey="value"
                        activeShape={renderActiveShape}
                        onMouseEnter={onPieEnter}
                        cornerRadius={3}
                    >
                        {allocations.map((entry, index) => (
                            <Cell key={`cell-${index}_${entry}`} fill={`url(#linearGradient${`${index}`})`} />
                        ))}
                    </Pie>
                    <Legend
                        content={(payload) => {
                            return (
                                <div
                                    className="flex flex-wrap pt-8 justify-center items-start"
                                    style={{ minHeight: '8rem' }}
                                >
                                    {payload.payload?.map((i, index) => (
                                        <div
                                            className="border-l-4 h-10 mb-2 flex flex-col mx-3"
                                            style={{ borderColor: i.color }}
                                            key={`${index}_${i.color}`}
                                        >
                                            <div className="text-left ml-2 leading-3 text-xs">{i.id}</div>
                                            <div className="text-left ml-2 text-xs">{i.value ? `${i.value}%` : ''}</div>
                                        </div>
                                    ))}
                                </div>
                            );
                        }}
                        payload={allocations.map((item, index) => ({
                            id: item.key,
                            type: 'square',
                            value: item.value,
                            color: COLORS[index % COLORS.length],
                        }))}
                    />
                </PieChart>
            </ResponsiveContainer>
        </div>
    );
};

export default AllocationCharts;

const renderActiveShape = (props: any) => {
    const { cx, cy, startAngle, endAngle, payload, innerRadius, outerRadius, z, cornerRadius, fill } = props;

    return (
        <g>
            <text
                x={cx}
                y={cy - 20}
                dy={8}
                fontSize={z ? '16px' : '24px'}
                textAnchor="middle"
                fill="#001233"
                fontWeight="bold"
            >
                {payload.value < 1000 ? `${payload.value}%` : `${parseFloat(String(payload.value / 1000)).toFixed(1)}K`}
            </text>
            <text x={cx} y={cy + 5} dy={8} fontSize="12px" textAnchor="middle" fill="#5C677D">
                {payload.key}
            </text>
            <Sector
                cx={cx}
                cy={cy}
                innerRadius={innerRadius}
                outerRadius={outerRadius + 10}
                startAngle={startAngle}
                endAngle={endAngle}
                fill={fill}
                cornerRadius={cornerRadius}
            />
            <Sector
                cx={cx}
                cy={cy}
                innerRadius={innerRadius - 18}
                outerRadius={innerRadius - 10}
                startAngle={startAngle}
                endAngle={endAngle}
                fill={fill}
            />
        </g>
    );
};
